import axiosInstance from './axiosInstance';
import { parseQueryOptions } from './queryHelpers';

export const authService = {
  login: (email, password) =>
    axiosInstance.post(`${process.env.REACT_APP_AUTH_API_URL}/login/`, { email, password }),

  passwordReset: (email) =>
    axiosInstance.post(`${process.env.REACT_APP_AUTH_API_URL}/password/reset/`, { email }),

  passwordResetConfirm: (uid, token, newPassword1, newPassword2) =>
    axiosInstance.post(
      `${process.env.REACT_APP_AUTH_API_URL}/password/reset/confirm/${uid}/${token}/`,
      { uid, token, new_password1: newPassword1, new_password2: newPassword2 },
    ),

  logout: () =>
    axiosInstance.post(`${process.env.REACT_APP_AUTH_API_URL}/logout/`),

  getUser: () =>
    axiosInstance.get(`${process.env.REACT_APP_AUTH_API_URL}/users/me/`),

  connectGoogle: (accessToken) =>
    axiosInstance.post(`${process.env.REACT_APP_AUTH_API_URL}/social/google/connect/`, {
      access_token: accessToken,
    }),

  disconnectGoogle: () =>
    axiosInstance.delete(`${process.env.REACT_APP_AUTH_API_URL}/social/google/disconnect/`),

  // Bootstrap endpoint - DOES NOT require X-Customer-ID
  getCollaboratorMe: () =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/me/`),

  // Self-service update of the authenticated collaborator's own personal
  // data (e.g. name). DOES NOT require X-Customer-ID.
  updateCollaboratorMe: (data) =>
    axiosInstance.patch(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/me/`, data),

  // Interface preferences of the authenticated collaborator (e.g. color
  // scheme). DOES NOT require X-Customer-ID.
  updateMyPreferences: (data) =>
    axiosInstance.patch(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/me/preferences/`, data),

  refreshToken: (refresh) =>
    axiosInstance.post(`${process.env.REACT_APP_AUTH_API_URL}/token/refresh/`, { refresh }),

  changePassword: (oldPassword, newPassword1, newPassword2) =>
    axiosInstance.post(`${process.env.REACT_APP_AUTH_API_URL}/password/change/`, {
      old_password: oldPassword,
      new_password1: newPassword1,
      new_password2: newPassword2,
    }),
};

// Customer-scoped services - Automatically include X-Customer-ID via interceptor
export const customerService = {
  // Businesses
  // Legacy slim list ({uuid, name}) of the logged-in collaborator's customer.
  getBusinesses: (queryOptions) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/customers/businesses/`, parseQueryOptions(queryOptions)),

  // Store management (dashboard): unpaginated; internal users may pass
  // `{ customer: <uuid> }`.
  listBusinesses: (params) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/businesses/`, { params }),

  getBusiness: (uuid) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/businesses/${uuid}/`),

  createBusiness: (data) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/businesses/`, data),

  patchBusiness: (uuid, data) =>
    axiosInstance.patch(`${process.env.REACT_APP_CUSTOMER_API_URL}/businesses/${uuid}/`, data),

  deleteBusiness: (uuid) =>
    axiosInstance.delete(`${process.env.REACT_APP_CUSTOMER_API_URL}/businesses/${uuid}/`),

  restoreBusiness: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/businesses/${uuid}/restore/`),

  // Headquarters a new branch of `customer` can hang from.
  getBusinessParentCompanies: (customer) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/businesses/parent-companies/`, {
      params: { customer },
    }),

  // Company persona `customer` already uses with this tax ID ({persona: null} if none).
  lookupBusinessPersona: ({ customer, taxId }) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/businesses/persona-lookup/`, {
      params: { customer, tax_id: taxId },
    }),

  // Address forms: postal code autofill and state/city options (by country).
  lookupPostalCode: ({ country, code }) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/postal-codes/lookup/`, {
      params: { country, code },
    }),

  getLocationStates: (country) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/locations/states/`, { params: { country } }),

  getLocationCities: (country, state) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/locations/cities/`, {
      params: { country, state },
    }),

  // Collaborators
  getCollaborators: (queryOptions) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/`, parseQueryOptions(queryOptions)),
  getCollaborator: (id) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/${id}/`),

  // Admin page (#602): the whole team in one page (API max), filtered in the
  // browser like Roles; removed collaborators included for the status filter.
  listAllCollaborators: () =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/`, {
      params: { pageSize: 1000, include_removed: true },
    }),

  // Collection-level capabilities (e.g. can_create for the "New" button).
  getCollaboratorsAuthorization: () =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/authorization/`),

  createCollaborator: (data) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/`, data),

  updateCollaborator: (uuid, data) =>
    axiosInstance.patch(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/${uuid}/`, data),

  deleteCollaborator: (uuid) =>
    axiosInstance.delete(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/${uuid}/`),

  restoreCollaborator: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/${uuid}/restore/`),

  // auth_service creates the user and emails the invitation; also resends.
  sendCollaboratorInvite: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/collaborators/${uuid}/send-invite/`),

  // Conversations
  getConversations: (queryOptions, config) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/`, { ...parseQueryOptions(queryOptions), ...config }),
  getConversation: (uuid, config) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/`, config),

  acceptConversation: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/accept/`),

  pinConversation: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/pin/`),

  unpinConversation: (uuid) =>
    axiosInstance.delete(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/pin/`),

  reorderPins: (uuids) =>
    axiosInstance.put(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/pins/`, { leads: uuids }),

  favoriteConversation: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/favorite/`),

  unfavoriteConversation: (uuid) =>
    axiosInstance.delete(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/favorite/`),

  // Lead lists (privadas por colaborador)
  getLeadLists: () =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-lists/`),

  createLeadList: (data) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-lists/`, data),

  updateLeadList: (uuid, data) =>
    axiosInstance.patch(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-lists/${uuid}/`, data),

  deleteLeadList: (uuid) =>
    axiosInstance.delete(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-lists/${uuid}/`),

  addLeadToList: (listUuid, leadUuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-lists/${listUuid}/leads/${leadUuid}/`),

  removeLeadFromList: (listUuid, leadUuid) =>
    axiosInstance.delete(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-lists/${listUuid}/leads/${leadUuid}/`),

  releaseConversation: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/release/`),

  closeConversation: (uuid, data) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/close/`, data),

  reopenConversation: (uuid, data = {}) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/reopen/`, data),

  transferConversation: (uuid, data) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/transfer/`, data),

  getConversationTimeline: (uuid) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/timeline/`),

  getMessages: (uuid, queryOptions, config) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/messages/`, { ...parseQueryOptions(queryOptions), ...config }),

  getMessageMediaAccess: (uuid, config) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/messages/${uuid}/media/`, config),

  replyMessage: (uuid, data) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/conversations/${uuid}/reply/`, data, {
      headers: { 'Content-Type': undefined },
    }),

  getContacts: (queryOptions, config) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/contacts/`, { ...parseQueryOptions(queryOptions), ...config }),

  getContact: (uuid) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/contacts/${uuid}/`),

  // Server-side: queryOptions (DataGrid) + start_date/end_date, which also
  // drop campaigns without leads in the period.
  getCampaigns: (queryOptions) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/campaigns/`, parseQueryOptions(queryOptions)),

  getCampaign: (uuid) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/campaigns/${uuid}/`),

  // Métricas: kind = overview | leads | ai | collaborators | conversion; params = { start_date?, end_date? }.
  getMetrics: (kind, params) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/metrics/${kind}/`, { params }),

  // Customers
  getCustomers: (queryOptions) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/customers/`, parseQueryOptions(queryOptions)),

  getCustomer: (uuid) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/customers/${uuid}/`),

  createCustomer: (data) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/customers/`, data),

  patchCustomer: (uuid, data) =>
    axiosInstance.patch(`${process.env.REACT_APP_CUSTOMER_API_URL}/customers/${uuid}/`, data),

  deleteCustomer: (uuid) =>
    axiosInstance.delete(`${process.env.REACT_APP_CUSTOMER_API_URL}/customers/${uuid}/`),

  restoreCustomer: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/customers/${uuid}/restore/`),

  // Providers
  getProviders: (queryOptions) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/providers/`, parseQueryOptions(queryOptions)),
  getProvider: (uuid) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/providers/${uuid}/`),

  // Roles
  getRoles: () =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/roles/`),

  getRole: (uuid) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/roles/${uuid}/`),

  createRole: (data) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/roles/`, data),

  updateRole: (uuid, data) =>
    axiosInstance.put(`${process.env.REACT_APP_CUSTOMER_API_URL}/roles/${uuid}/`, data),

  patchRole: (uuid, data) =>
    axiosInstance.patch(`${process.env.REACT_APP_CUSTOMER_API_URL}/roles/${uuid}/`, data),

  deleteRole: (uuid) =>
    axiosInstance.delete(`${process.env.REACT_APP_CUSTOMER_API_URL}/roles/${uuid}/`),

  restoreRole: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/roles/${uuid}/restore/`),

  // Lead Origins
  getLeadOrigins: () =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-origins/`),

  getLeadOrigin: (uuid) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-origins/${uuid}/`),

  createLeadOrigin: (data) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-origins/`, data),

  updateLeadOrigin: (uuid, data) =>
    axiosInstance.put(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-origins/${uuid}/`, data),

  deleteLeadOrigin: (uuid) =>
    axiosInstance.delete(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-origins/${uuid}/`),

  restoreLeadOrigin: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/conversations/lead-origins/${uuid}/restore/`),

  // Departments
  getDepartments: () =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/departments/`),

  getDepartment: (uuid) =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/departments/${uuid}/`),

  createDepartment: (data) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/departments/`, data),

  updateDepartment: (uuid, data) =>
    axiosInstance.put(`${process.env.REACT_APP_CUSTOMER_API_URL}/departments/${uuid}/`, data),

  deleteDepartment: (uuid) =>
    axiosInstance.delete(`${process.env.REACT_APP_CUSTOMER_API_URL}/departments/${uuid}/`),

  restoreDepartment: (uuid) =>
    axiosInstance.post(`${process.env.REACT_APP_CUSTOMER_API_URL}/departments/${uuid}/restore/`),

  // Permissions
  getPermissionsCatalog: () =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/permissions/`),

  // Debug
  getContextDebug: () =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/context-debug/`),

  getSchema: () =>
    axiosInstance.get(`${process.env.REACT_APP_CUSTOMER_API_URL}/schema/`),
};

// Each service that gates endpoints on role codes serves its own catalog in
// the same shape; the permissions page merges them (#601).
export const crmService = {
  getPermissionsCatalog: () =>
    axiosInstance.get(`${process.env.REACT_APP_CRM_API_URL}/permissions/`),

  listIntegrations: () =>
    axiosInstance.get(`${process.env.REACT_APP_CRM_API_URL}/integrations/`),

  // type = 'crm' | 'stock': the two config tables share the same CRUD shape.
  getIntegrationConfig: (type, id) =>
    axiosInstance.get(`${process.env.REACT_APP_CRM_API_URL}/${type}-configs/${id}/`),

  createIntegrationConfig: (type, data) =>
    axiosInstance.post(`${process.env.REACT_APP_CRM_API_URL}/${type}-configs/`, data),

  // credentials is a JSON merge patch: keys omitted keep the stored value.
  updateIntegrationConfig: (type, id, data) =>
    axiosInstance.patch(`${process.env.REACT_APP_CRM_API_URL}/${type}-configs/${id}/`, data),

  deleteIntegrationConfig: (type, id) =>
    axiosInstance.delete(`${process.env.REACT_APP_CRM_API_URL}/${type}-configs/${id}/`),

  restoreIntegrationConfig: (type, id) =>
    axiosInstance.post(`${process.env.REACT_APP_CRM_API_URL}/${type}-configs/${id}/restore/`),

  authorizeDealerspace: (id, code) =>
    axiosInstance.post(`${process.env.REACT_APP_CRM_API_URL}/crm-configs/${id}/dealerspace/authorize/`, { code }),

  listExportAttempts: (params) =>
    axiosInstance.get(`${process.env.REACT_APP_CRM_API_URL}/export-attempts/`, { params }),

  retryExportAttempt: (id) =>
    axiosInstance.post(`${process.env.REACT_APP_CRM_API_URL}/export-attempts/${id}/retry/`),

  listStockSyncAttempts: (params) =>
    axiosInstance.get(`${process.env.REACT_APP_CRM_API_URL}/stock-sync-attempts/`, { params }),

  syncStockConfig: (id) =>
    axiosInstance.post(`${process.env.REACT_APP_CRM_API_URL}/stock-configs/${id}/sync/`),
};

export const notificationService = {
  postSubscription: (data, deviceName) =>
    axiosInstance.post(`${process.env.REACT_APP_NOTIFICATION_API_URL}/subscriptions/`, data, {
      headers: { 'X-Device-Name': deviceName }
    }),
  ackNotification: (deliveryId) =>
    axiosInstance.post(`${process.env.REACT_APP_NOTIFICATION_API_URL}/ack/${deliveryId}`),
  getPublicKey: () =>
    axiosInstance.get(`${process.env.REACT_APP_NOTIFICATION_API_URL}/public-key/`),
  getSettings: () =>
    axiosInstance.get(`${process.env.REACT_APP_NOTIFICATION_API_URL}/settings/`),
  // X-Customer-ID / X-Collaborator-ID are added by the request interceptor
  getNotificationSettings: () =>
    axiosInstance.get(`${process.env.REACT_APP_NOTIFICATION_API_URL}/notification-settings/`),
  updateNotificationSettings: (id, data) =>
    axiosInstance.put(`${process.env.REACT_APP_NOTIFICATION_API_URL}/notification-settings/${id}`, data),
}
